/**
 * GA4 / Firebase Analytics helpers for the booking funnel.
 *
 * Every helper is a thin, named wrapper around `logEvent` so call sites stay
 * declarative and the GA4 ecommerce schema lives in one place. Events follow
 * the recommended ecommerce funnel (view_item → select_item → add_to_cart →
 * begin_checkout → purchase) plus a few custom intent events that capture where
 * users enter the flow and where they drop off.
 *
 * Analytics is only initialised in production (see `firebase.ts`), so in dev /
 * unsupported environments these calls no-op (and log to the console in DEV).
 */
import { logEvent } from 'firebase/analytics';
import {
  bookingTotal,
  lineTotalForParticipant,
  type AvailabilitySlot,
  type CardPaymentStatus,
  type ExperienceOption,
  type PaymentMethod,
} from '@flyingmuse/shared';
import type { Experience } from '../data/experiences';
import { analytics } from './firebase';

/** All pricing is in euros. */
export const ANALYTICS_CURRENCY = 'EUR';

/**
 * Human-readable `payment_type` values for GA4's ecommerce events. Every
 * checkout event carries one so the two settlement routes can be compared —
 * conversion rate, average order value, drop-off — rather than blurred together.
 */
const PAYMENT_TYPE: Record<PaymentMethod, string> = {
  card: 'Card',
  arrival: 'Pay on arrival',
};

/** Where a booking flow was opened from — captures user intent / attribution. */
export type BookingSource =
  | 'header_desktop'
  | 'header_mobile'
  | 'hero'
  | 'cta_section'
  | 'experiences_page'
  | 'experience_card'
  | 'experience_detail_hero'
  | 'experience_detail_footer'
  | 'package_card'
  | 'unknown';

type Params = Record<string, unknown>;

/** Fire a GA4 event. Never throws; no-ops when analytics is unavailable. */
function track(name: string, params: Params = {}): void {
  try {
    if (!analytics) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('[ga:dev]', name, params);
      }
      return;
    }
    logEvent(analytics, name, params as never);
  } catch {
    /* Analytics must never break the booking UX. */
  }
}

/** Round to 2dp to keep GA4 revenue figures clean. */
function money(n: number): number {
  return Math.round(n * 100) / 100;
}

function totalGuests(quantities: Record<string, number>): number {
  return Object.values(quantities).reduce((n, q) => n + q, 0);
}

/** Common GA4 `items[]` fields shared across the funnel. */
function baseItem(experience: Experience, option?: ExperienceOption): Params {
  return {
    item_id: experience.productAlias,
    item_name: experience.pageTitle,
    item_brand: 'Flying Muse',
    item_category: experience.kicker,
    ...(option ? { item_variant: option.title } : {}),
  };
}

/**
 * Build a GA4 `items[]` array. When a priced slot + guest quantities are known
 * we emit one line per participant type (accurate per-unit price); otherwise we
 * fall back to a single summary line using the option/experience "from" price.
 */
function buildItems(
  experience: Experience,
  option?: ExperienceOption,
  quantities?: Record<string, number>,
  slot?: AvailabilitySlot,
): Params[] {
  if (option && quantities && slot) {
    const lines = option.participantTypes
      .map((pt) => {
        const qty = quantities[pt.alias] ?? 0;
        if (qty <= 0) return null;
        const pricing = slot.pricing.find(
          (p) => p.participantTypeAlias === pt.alias,
        );
        const lineTotal = lineTotalForParticipant(pricing, qty);
        return {
          ...baseItem(experience, option),
          item_variant: `${option.title} · ${pt.name}`,
          price: money(lineTotal / qty),
          quantity: qty,
        } as Params;
      })
      .filter((l): l is Params => l !== null);
    if (lines.length) return lines;
  }

  const price = option ? option.lowestPrice : experience.fromPrice;
  return [{ ...baseItem(experience, option), price, quantity: 1 }];
}

/** Total cart value for the current selection. */
function cartValue(
  option?: ExperienceOption,
  quantities?: Record<string, number>,
  slot?: AvailabilitySlot,
): number {
  if (slot && quantities) return money(bookingTotal(slot, quantities));
  if (option && quantities) {
    return money(option.lowestPrice * Math.max(1, totalGuests(quantities)));
  }
  if (option) return option.lowestPrice;
  return 0;
}

/* ------------------------------------------------------------------ */
/* Intent events                                                       */
/* ------------------------------------------------------------------ */

/** User opened the booking flow — captures the entry point (attribution). */
export function trackBookingOpen(
  source: BookingSource,
  experience?: Experience,
): void {
  track('booking_open', {
    booking_source: source,
    ...(experience
      ? { item_id: experience.productAlias, item_name: experience.pageTitle }
      : {}),
  });
}

/** A user viewed an experience detail page. */
export function trackViewExperience(experience: Experience): void {
  track('view_item', {
    currency: ANALYTICS_CURRENCY,
    value: experience.fromPrice,
    items: buildItems(experience),
  });
}

/* ------------------------------------------------------------------ */
/* Funnel events                                                       */
/* ------------------------------------------------------------------ */

/** Step 1 — an experience was chosen inside the wizard. */
export function trackSelectExperience(experience: Experience): void {
  track('select_item', {
    item_list_name: 'booking_wizard',
    items: buildItems(experience),
  });
}

/** Step 2 — a package/option was chosen (treated as add_to_cart). */
export function trackSelectOption(
  experience: Experience,
  option: ExperienceOption,
): void {
  track('add_to_cart', {
    currency: ANALYTICS_CURRENCY,
    value: cartValue(option),
    items: buildItems(experience, option),
  });
}

/** Step 3 — a date/slot was selected. Custom intent signal. */
export function trackSelectDate(
  experience: Experience,
  option: ExperienceOption,
  slot: AvailabilitySlot,
  quantities: Record<string, number>,
): void {
  track('select_date', {
    currency: ANALYTICS_CURRENCY,
    value: cartValue(option, quantities, slot),
    booking_date: slot.dateTime,
    guests: totalGuests(quantities),
    items: buildItems(experience, option, quantities, slot),
  });
}

/** Step 4 — user committed date + guests and is entering checkout. */
export function trackBeginCheckout(
  experience: Experience,
  option: ExperienceOption,
  slot: AvailabilitySlot,
  quantities: Record<string, number>,
): void {
  track('begin_checkout', {
    currency: ANALYTICS_CURRENCY,
    value: cartValue(option, quantities, slot),
    guests: totalGuests(quantities),
    items: buildItems(experience, option, quantities, slot),
  });
}

/** Step 5 — contact details captured; moving to the review step. */
export function trackAddContactInfo(
  experience: Experience,
  option: ExperienceOption,
  slot: AvailabilitySlot,
  quantities: Record<string, number>,
): void {
  track('add_contact_info', {
    currency: ANALYTICS_CURRENCY,
    value: cartValue(option, quantities, slot),
    items: buildItems(experience, option, quantities, slot),
  });
}

/**
 * The guest switched the payment toggle on the review step. Custom event, and
 * the one that answers "should card be the default?" — it counts preference
 * even for people who then abandon, which `add_payment_info` would miss.
 */
export function trackSelectPaymentMethod(
  paymentMethod: PaymentMethod,
  experience?: Experience,
): void {
  track('select_payment_method', {
    payment_type: PAYMENT_TYPE[paymentMethod],
    ...(experience ? { item_id: experience.productAlias } : {}),
  });
}

/**
 * Step 6 — the guest committed to a payment method by confirming. GA4's
 * standard checkout step; completes the recommended funnel
 * (view_item → select_item → add_to_cart → begin_checkout → add_payment_info →
 * purchase), which is what makes the built-in funnel reports work.
 */
export function trackAddPaymentInfo(
  experience: Experience,
  option: ExperienceOption,
  slot: AvailabilitySlot,
  quantities: Record<string, number>,
  paymentMethod: PaymentMethod,
): void {
  track('add_payment_info', {
    payment_type: PAYMENT_TYPE[paymentMethod],
    currency: ANALYTICS_CURRENCY,
    value: cartValue(option, quantities, slot),
    guests: totalGuests(quantities),
    booking_date: slot.dateTime,
    items: buildItems(experience, option, quantities, slot),
  });
}

/** Conversion — booking confirmed. `transactionId` is the LinkTwist code. */
export function trackPurchase(
  experience: Experience,
  option: ExperienceOption,
  slot: AvailabilitySlot,
  quantities: Record<string, number>,
  transactionId: string,
  paymentMethod: PaymentMethod = 'arrival',
): void {
  track('purchase', {
    transaction_id: transactionId,
    payment_type: PAYMENT_TYPE[paymentMethod],
    currency: ANALYTICS_CURRENCY,
    value: cartValue(option, quantities, slot),
    guests: totalGuests(quantities),
    booking_date: slot.dateTime,
    items: buildItems(experience, option, quantities, slot),
  });
}

/**
 * The guest is being handed over to Smart Checkout — the last event we can fire
 * before the browser leaves the site. Paired with `trackCardPurchase` on the way
 * back, this is what measures how many people we lose *on Viva's page*, which
 * is otherwise a complete blind spot.
 */
export function trackCardCheckoutStart(
  experience: Experience,
  option: ExperienceOption,
  slot: AvailabilitySlot,
  quantities: Record<string, number>,
  orderCode: string,
): void {
  track('card_checkout_start', {
    transaction_id: orderCode,
    payment_type: PAYMENT_TYPE.card,
    currency: ANALYTICS_CURRENCY,
    value: cartValue(option, quantities, slot),
    guests: totalGuests(quantities),
    booking_date: slot.dateTime,
    items: buildItems(experience, option, quantities, slot),
  });
}

/**
 * Conversion — a card payment cleared on the return page. Fired without the
 * wizard's objects to hand (the guest came back from an off-site redirect), so
 * this takes the plain figures kept in the checkout handoff.
 */
export function trackCardPurchase(params: {
  bookingCode: string;
  value: number;
  guests: number;
  experienceAlias: string | null;
  experienceTitle: string | null;
  optionTitle: string | null;
  bookingDate: string;
}): void {
  track('purchase', {
    transaction_id: params.bookingCode,
    payment_type: PAYMENT_TYPE.card,
    currency: ANALYTICS_CURRENCY,
    value: money(params.value),
    guests: params.guests,
    booking_date: params.bookingDate,
    items: [
      {
        item_id: params.experienceAlias ?? 'unknown',
        item_name: params.experienceTitle ?? 'Flying Muse experience',
        item_brand: 'Flying Muse',
        ...(params.optionTitle ? { item_variant: params.optionTitle } : {}),
        price: money(params.value),
        quantity: 1,
      },
    ],
  });
}

/**
 * The guest came back from Smart Checkout. Fired for every outcome *including*
 * success, so `card_checkout_start` minus `card_checkout_return` gives the
 * number who never came back at all — abandoned on Viva's page, or lost in the
 * redirect. Without this, those two failure modes look identical.
 */
export function trackCardCheckoutReturn(
  status: CardPaymentStatus | 'unresolved',
  orderCode: string,
): void {
  track('card_checkout_return', {
    transaction_id: orderCode,
    payment_type: PAYMENT_TYPE.card,
    payment_status: status,
  });
}

/**
 * A card payment did not go through — declined, cancelled, or returned without
 * a transaction. Not an error event: this is an expected outcome worth counting
 * against `card_checkout_start` as a decline rate.
 */
export function trackCardPaymentFailed(
  orderCode: string | null,
  reason: string,
): void {
  track('card_payment_failed', {
    ...(orderCode ? { transaction_id: orderCode } : {}),
    payment_type: PAYMENT_TYPE.card,
    reason,
  });
}

/**
 * Money was taken but the booking could not be confirmed automatically, or the
 * outcome was still unresolved when the guest gave up waiting. Rare by design —
 * worth marking as a GA4 conversion so it's alerted on rather than discovered.
 */
export function trackCardPaymentUnresolved(
  orderCode: string,
  status: 'needsAttention' | 'timeout',
  value?: number,
): void {
  track('card_payment_unresolved', {
    transaction_id: orderCode,
    payment_type: PAYMENT_TYPE.card,
    payment_status: status,
    ...(value != null ? { currency: ANALYTICS_CURRENCY, value: money(value) } : {}),
  });
}

/* ------------------------------------------------------------------ */
/* WhatsApp                                                            */
/* ------------------------------------------------------------------ */

/**
 * Where a guest left for WhatsApp. Worth splitting because these are two very
 * different signals wearing the same coat:
 *   - intent — they'd rather talk to a person before booking
 *   - breakage — the site could not serve them, so WhatsApp is the escape hatch
 * Lumped together they average into a number that means nothing.
 */
export type WhatsAppSource =
  // Intent
  | 'floating_button'
  | 'experience_detail_hero'
  | 'experience_detail_footer'
  | 'about_studio'
  | 'about_footer'
  | 'footer'
  | 'package_enquiry'
  // Breakage
  | 'package_card'
  | 'booking_fallback'
  | 'payment_failed'
  | 'payment_unresolved';

/**
 * The guest is leaving for WhatsApp. Mark `whatsapp_click` as a key event in
 * GA4 and break it down by `source`.
 *
 * Fired on click rather than on unload: every one of these links opens in a new
 * tab, so this page stays alive and the event has time to leave.
 */
export function trackWhatsAppClick(
  source: WhatsAppSource,
  experience?: Experience,
  context?: string,
): void {
  track('whatsapp_click', {
    source,
    ...(experience
      ? { item_id: experience.productAlias, item_name: experience.pageTitle }
      : {}),
    ...(context ? { context } : {}),
  });
}

/* ------------------------------------------------------------------ */
/* Drop-off events                                                     */
/* ------------------------------------------------------------------ */

/** A booking submission failed. Helps quantify lost conversions. */
export function trackBookingError(
  step: string,
  message: string,
  experience?: Experience,
): void {
  track('booking_error', {
    booking_step: step,
    error_message: message,
    ...(experience ? { item_id: experience.productAlias } : {}),
  });
}

/** The wizard was closed before a successful booking. */
export function trackBookingAbandon(
  step: string,
  experience?: Experience,
): void {
  track('booking_abandon', {
    booking_step: step,
    ...(experience
      ? { item_id: experience.productAlias, item_name: experience.pageTitle }
      : {}),
  });
}
